// ArcGauge — semicircular progress gauge (e.g. "% of retirement goal"). Custom
// SVG with a gradient stroke whose hue shifts by tone. Animated dash on value
// change. Used on the Dashboard readiness card and Manage Account. From the
// prototype's ArcGauge.
import PropTypes from 'prop-types';

const GRADIENTS = {
  default: ['#0077CC', '#552299'],
  behind: ['#FFBB11', '#FF6600'],
  ahead: ['#0077CC', '#119944'],
};

export function ArcGauge({ pct, tone = 'default', label = 'OF RETIREMENT GOAL', className, displayPct }) {
  const W = 260;
  const H = 134;
  const cx = W / 2;
  const cy = 124;
  const r = 100;
  const arcLen = Math.PI * r;
  const clamped = Math.max(0, Math.min(100, pct));
  const dash = (clamped / 100) * arcLen;
  const angle = Math.PI - (clamped / 100) * Math.PI;
  const endX = cx + r * Math.cos(angle);
  const endY = cy - r * Math.sin(angle);
  const [a, b] = GRADIENTS[tone] || GRADIENTS.default;
  const gid = `gauge-${tone}`;
  // The ARC saturates at 100% — a semicircle cannot draw more — but the NUMBER
  // shouldn't. "Ahead of goal" cases (104%, 125%) previously both rendered "100%"
  // because the readout reused the clamped geometry value, so two different
  // retirement ages looked identical. Callers with a true figure pass it here;
  // everyone else keeps the clamped value.
  const readout = displayPct ?? clamped;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className={className} role="img" aria-label={`${readout}% ${label}`}>
      <defs>
        <linearGradient id={gid} x1="0" x2="1" y1="0" y2="0">
          <stop offset="0%" stopColor={a} />
          <stop offset="100%" stopColor={b} />
        </linearGradient>
      </defs>
      <path
        d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`}
        stroke="var(--bg-sunken)"
        strokeWidth="14"
        fill="none"
        strokeLinecap="round"
      />
      <path
        d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`}
        stroke={`url(#${gid})`}
        strokeWidth="14"
        fill="none"
        strokeLinecap="round"
        strokeDasharray={`${dash} ${arcLen}`}
        style={{ transition: 'stroke-dasharray .9s cubic-bezier(.4,0,.2,1)' }}
      />
      <circle
        cx={endX}
        cy={endY}
        r="8"
        fill="var(--bg-card)"
        stroke={`url(#${gid})`}
        strokeWidth="3"
        style={{ transition: 'cx .9s, cy .9s' }}
      />
      <text x={cx} y={cy - 32} textAnchor="middle" fontFamily="Plus Jakarta Sans, system-ui" fontWeight="700" fontSize="38" letterSpacing="-1" fill="var(--text-1)">
        {readout}
        <tspan fontSize="20" dx="2">%</tspan>
      </text>
      <text x={cx} y={cy - 10} textAnchor="middle" fontFamily="Inter, system-ui" fontWeight="600" fontSize="11" letterSpacing="0.06em" fill="var(--text-3)">
        {label}
      </text>
    </svg>
  );
}

ArcGauge.propTypes = {
  /** Drives the arc geometry. Clamped to 0–100 for drawing. */
  pct: PropTypes.number.isRequired,
  tone: PropTypes.oneOf(['default', 'behind', 'ahead']),
  label: PropTypes.string,
  className: PropTypes.string,
  /**
   * The figure to READ OUT, when it differs from the drawn one — i.e. when the true
   * value exceeds 100. Defaults to the clamped `pct`.
   */
  displayPct: PropTypes.number,
};

export default ArcGauge;
